if (!customElements.get('campaign-product-offer')) {
  customElements.define('campaign-product-offer', class extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      const options = { signal: this.lifecycle.signal };
      this.panel = this.querySelector('[data-offer-panel]');
      this.panel.style.cssText = this.style.cssText;
      this.button = this.panel.querySelector('[data-offer-add]');
      this.feedback = this.panel.querySelector('[data-offer-feedback]');
      this.status = this.panel.querySelector('[data-offer-status]');
      this.products = [...this.querySelectorAll('[data-offer-product]')];
      this.completed = this.completed || this.dataset.pairInCart === 'true';
      this.mobileViewport = window.matchMedia('(max-width: 749px)');
      this.mobileViewport.addEventListener('change', () => this.placePanel(), options);
      window.addEventListener('resize', () => { this.checkHomeBanner(); this.placePanel(); }, options);
      document.addEventListener('scroll', () => {
        if (this.placementFrame || this.panel.hidden || !this.mobileViewport.matches) return;
        this.placementFrame = requestAnimationFrame(() => {
          this.placementFrame = null;
          this.placePanel();
        });
      }, { ...options, capture: true, passive: true });
      this.startAt = this.scheduleTimestamp(this.dataset.startDate, this.dataset.startTime);
      this.endAt = this.dataset.endDate?.trim() ? this.scheduleTimestamp(this.dataset.endDate, this.dataset.endTime) : null;
      this.serverNow = Number(this.dataset.serverNow) * 1000;
      this.serverMeasuredAt = performance.now();
      this.homePassed = this.dataset.homepage !== 'true';
      this.button.addEventListener('click', () => this.addPair(), options);
      const recheck = () => {
        this.checkHomeBanner();
        this.updateVisibility();
        this.armScheduleTimer();
      };
      document.addEventListener('visibilitychange', recheck, options);
      window.addEventListener('pageshow', recheck, options);
      if (this.dataset.designMode === 'true') {
        this.homePassed = true;
        this.updateVisibility();
        return;
      }
      if (!this.homePassed) {
        this.homeBanner = document.querySelector('#MainContent .yg-ss')?.closest('.shopify-section') ||
          document.querySelector('#MainContent > .shopify-section');
        if (this.homeBanner) {
          this.heroObserver = new IntersectionObserver(() => this.checkHomeBanner(), { threshold: 0 });
          this.heroObserver.observe(this.homeBanner);
        }
        this.checkHomeBanner();
      }
      this.updateVisibility();
      this.armScheduleTimer();
      this.cart().then((cart) => {
        if (!this.pending && this.products.every((product) => this.quantity(cart, product.dataset.variantId) > 0)) this.completed = true;
      }).catch(() => {}).finally(() => {
        if (this.isConnected) { this.updateVisibility(); this.armScheduleTimer(); }
      });
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = null;
      this.heroObserver?.disconnect();
      clearTimeout(this.scheduleTimer);
      cancelAnimationFrame(this.placementFrame);
      this.placementFrame = null;
      this.resetPlacement();
    }

    checkHomeBanner() {
      if (this.homePassed) return;
      if (!this.homeBanner || this.homeBanner.getBoundingClientRect().bottom <= 0) {
        this.homePassed = true;
        this.heroObserver?.disconnect();
        this.updateVisibility();
      }
    }

    updateVisibility() {
      if (!this.isConnected) return;
      const preview = this.dataset.designMode === 'true';
      const visible = preview || (!this.completed && this.homePassed && this.scheduleOpen());
      this.panel.hidden = !visible;
      this.button.disabled = preview || this.pending || this.uncertain || !this.eligible();
      if (!this.eligible()) this.button.textContent = this.dataset.unavailable;
      else this.button.textContent = this.dataset.buttonLabel;
      if (visible) this.watchCart();
      else this.resetPlacement();
    }

    scheduleTimestamp(dateValue, timeValue) {
      const date = dateValue?.trim();
      const time = timeValue?.trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '') || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time || '')) return NaN;
      const [year, month, day] = date.split('-').map(Number);
      const [hour, minute] = time.split(':').map(Number);
      const wallTime = Date.UTC(year, month - 1, day, hour, minute);
      if (new Date(wallTime).toISOString().slice(0, 10) !== date) return NaN;
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: this.dataset.scheduleTimezone, calendar: 'gregory', numberingSystem: 'latn',
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
        });
        if (!this.dataset.scheduleTimezone?.trim()) return NaN;
        let instant = wallTime;
        for (let attempt = 0; attempt < 4; attempt++) {
          const parts = Object.fromEntries(formatter.formatToParts(instant).map(({ type, value }) => [type, value]));
          const local = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute));
          if (local === wallTime) return instant;
          instant += wallTime - local;
        }
      } catch { return NaN; }
      return NaN;
    }

    scheduleOpen() {
      if (this.dataset.scheduleEnabled !== 'true') return true;
      const now = this.serverTime();
      return Number.isFinite(this.startAt) && Number.isFinite(now) && now >= this.startAt &&
        (this.endAt === null || (Number.isFinite(this.endAt) && this.endAt > this.startAt && now < this.endAt));
    }

    serverTime() {
      return this.serverNow + Math.max(0, performance.now() - this.serverMeasuredAt);
    }

    armScheduleTimer() {
      clearTimeout(this.scheduleTimer);
      if (this.dataset.designMode === 'true' || this.dataset.scheduleEnabled !== 'true') return;
      const now = this.serverTime();
      const next = [this.startAt, this.endAt].filter((at) => Number.isFinite(at) && at > now).sort((a, b) => a - b)[0];
      if (!next) return;
      this.scheduleTimer = setTimeout(() => {
        this.updateVisibility();
        this.armScheduleTimer();
      }, Math.min(next - now + 20, 2147483647));
    }

    eligible() {
      return this.products.length === 2 && new Set(this.products.map((product) => product.dataset.variantId)).size === 2 &&
        this.products.every((product) => product.dataset.available === 'true' && /^\d+$/.test(product.dataset.variantId) &&
          !window.ForcedOutOfStock?.isBlockedVariant(product.dataset.variantId));
    }

    async cart() {
      const response = await fetch(this.dataset.cartUrl, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error(this.dataset.error);
      const serverNow = Date.parse(response.headers.get('Date'));
      if (Number.isFinite(serverNow)) {
        this.serverNow = serverNow;
        this.serverMeasuredAt = performance.now();
      }
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error(this.dataset.error);
      return cart;
    }

    quantity(cart, variantId) {
      return cart.items.filter((item) => String(item.variant_id) === String(variantId) && !item.selling_plan_allocation && !item.selling_plan)
        .reduce((sum, item) => sum + item.quantity, 0);
    }

    sections() {
      const cart = document.querySelector('cart-drawer, cart-notification');
      const ids = cart?.getSectionsToRender?.().map((section) => section.id) || [];
      if (!ids.includes('cart-icon-bubble')) ids.push('cart-icon-bubble');
      return ids;
    }

    async postPair() {
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: this.products.map((product) => ({ id: product.dataset.variantId, quantity: 1 })),
          sections: this.sections().join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : this.dataset.error);
      return result;
    }

    showFeedback(message) {
      this.status.textContent = message;
      this.feedback.hidden = false;
      this.placePanel();
    }

    async addPair() {
      if (this.completed || this.pending || this.uncertain || !this.eligible() || this.dataset.designMode === 'true') return;
      this.pending = true;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.feedback.hidden = true;
      this.initiator = document.getElementById('cart-icon-bubble') || this.button;
      let before;
      let posted = false;
      let added = false;
      try {
        before = await this.cart();
        if (!this.isConnected || !this.scheduleOpen()) { this.updateVisibility(); return; }
        posted = true;
        const result = await this.postPair();
        added = true;
        if (!this.isConnected) return;
        this.completed = true;
        this.updateVisibility();
        this.showFeedback(this.dataset.added);
        await this.refreshCart(result, true);
      } catch (error) {
        if (!this.isConnected) return;
        if (posted && !added) {
          try {
            const after = await this.cart();
            const changes = this.products.map((product) => this.quantity(after, product.dataset.variantId) - this.quantity(before, product.dataset.variantId));
            added = changes.every((quantity) => quantity >= 1);
            this.uncertain = !added && changes.some((quantity) => quantity !== 0);
          } catch { this.uncertain = true; }
          if (added) {
            this.completed = true;
            this.updateVisibility();
            try { await this.refreshCart(null, true); } catch {}
          }
        }
        this.showFeedback(this.uncertain ? this.dataset.uncertain : added ? this.dataset.added : error.message || this.dataset.error);
      } finally {
        this.pending = false;
        this.button.removeAttribute('aria-busy');
        this.updateVisibility();
        this.armScheduleTimer();
      }
    }

    watchCart() {
      if (this.dataset.designMode === 'true') return;
      if (!this.cartObserver) this.cartObserver = new MutationObserver(() => this.placePanel());
      this.cartObserver.observe(document.body, { childList: true, subtree: true });
      this.placePanel();
    }

    placePanel() {
      if (this.panel.hidden || !this.isConnected) return;
      const drawer = document.querySelector('#occ3-drawer');
      if (drawer !== this.observedDrawer) {
        this.observedDrawer = drawer;
        if (drawer) this.cartObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }
      const open = drawer?.classList.contains('occ3-display-block');
      const checkout = open && this.mobileViewport.matches ? drawer.querySelector('[data-occ3="checkout"]') : null;
      const target = checkout?.parentElement;
      const parent = target || this;
      this.panel.toggleAttribute('data-in-cart', Boolean(target));
      this.panel.toggleAttribute('data-cart-notification', Boolean(checkout));
      if (this.panel.parentElement !== parent || (checkout && this.panel.nextElementSibling !== checkout)) {
        const focused = this.panel.contains(document.activeElement) ? document.activeElement : null;
        parent.insertBefore(this.panel, checkout || parent.firstChild);
        focused?.focus({ preventScroll: true });
      }
      if (checkout) {
        const bounds = checkout.getBoundingClientRect();
        const fitsBelow = window.innerHeight - bounds.bottom >= this.panel.offsetHeight + 24;
        this.panel.style.setProperty('--pair-toast-bottom', `${fitsBelow ? 12 : Math.max(12, window.innerHeight - bounds.top + 12)}px`);
      } else {
        this.panel.style.removeProperty('--pair-toast-bottom');
      }
    }

    resetPlacement() {
      this.cartObserver?.disconnect();
      this.observedDrawer = null;
      this.panel.removeAttribute('data-in-cart');
      this.panel.removeAttribute('data-cart-notification');
      this.panel.style.removeProperty('--pair-toast-bottom');
      if (this.panel.parentElement !== this) this.append(this.panel);
    }

    async refreshCart(result, open) {
      if (result?.sections?.['cart-icon-bubble']) {
        const bubble = document.getElementById('cart-icon-bubble');
        const html = new DOMParser().parseFromString(result.sections['cart-icon-bubble'], 'text/html');
        const content = html.querySelector('.shopify-section');
        if (bubble && content) bubble.innerHTML = content.innerHTML;
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'campaign-product-offer', cartData: result });
      }
      const cartly = window.Spurit?.OneClickCheckout3?.cartDrawer;
      if (typeof cartly?.refresh === 'function') {
        await cartly.refresh();
        if (open && typeof cartly.show === 'function') await cartly.show();
        return;
      }
      if (!open) return;
      const cart = document.querySelector('cart-drawer, cart-notification');
      if (result?.sections && cart?.renderContents && cart.getSectionsToRender().every((section) => result.sections[section.id])) {
        cart.setActiveElement?.(this.initiator);
        cart.classList.remove('is-empty');
        cart.renderContents(result);
      } else {
        window.location.assign(this.dataset.viewCartUrl);
      }
    }
  });
}
